/**
 * Platform-agnostic authentication manager
 *
 * Uses storage adapter pattern for cross-platform compatibility.
 * Configurable key prefix for multi-tenant or multi-app environments.
 */

interface StorageAdapter {
  get(key: string): Promise<string | null>
  set(key: string, value: string): Promise<void>
  remove(key: string): Promise<void>
}

interface AuthManagerOptions {
  keyPrefix?: string
}

export class AuthManager {
  storage: StorageAdapter
  client: any
  refreshPromise: Promise<string | null> | null
  keyPrefix: string

  constructor(storage: StorageAdapter, options: AuthManagerOptions = {}) {
    if (!storage) {
      throw new Error('AuthManager requires a storage adapter')
    }
    this.storage = storage
    this.client = null
    this.refreshPromise = null
    this.keyPrefix = options.keyPrefix || 'realm_'
  }

  /** Get storage key with prefix */
  _getKey(key: string): string {
    return `${this.keyPrefix}${key}`
  }

  /** Set RPC client for refresh token operations */
  setClient(client: any): void {
    this.client = client
  }

  async getAccessToken(): Promise<string | null> {
    return await this.storage.get(this._getKey('access_token'))
  }

  async setAccessToken(token: string): Promise<void> {
    if (!token) throw new Error('Token is required')
    await this.storage.set(this._getKey('access_token'), token)
  }

  async clearAccessToken(): Promise<void> {
    try {
      await this.storage.remove(this._getKey('access_token'))
    } catch (error) {
      console.error('[AuthManager] Failed to clear access token:', error)
    }
  }

  async getRefreshToken(): Promise<string | null> {
    return await this.storage.get(this._getKey('refresh_token'))
  }

  async setRefreshToken(token: string): Promise<void> {
    if (!token) throw new Error('Token is required')
    await this.storage.set(this._getKey('refresh_token'), token)
  }

  async clearRefreshToken(): Promise<void> {
    try {
      await this.storage.remove(this._getKey('refresh_token'))
    } catch (error) {
      console.error('[AuthManager] Failed to clear refresh token:', error)
    }
  }

  async setTokens(accessToken: string, refreshToken?: string): Promise<void> {
    await this.setAccessToken(accessToken)
    if (refreshToken) {
      await this.setRefreshToken(refreshToken)
    }
  }

  async clearTokens(): Promise<void> {
    await Promise.all([this.clearAccessToken(), this.clearRefreshToken()])
  }

  decodeJWT(token: string): any {
    try {
      const parts = token.split('.')
      if (parts.length !== 3) {
        return null
      }

      const payload = parts[1]
      const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'))
      return JSON.parse(decoded)
    } catch (error) {
      console.error('[AuthManager] Failed to decode JWT:', error)
      return null
    }
  }

  isTokenExpired(token: string): boolean {
    const payload = this.decodeJWT(token)
    if (!payload || !payload.exp) {
      return true
    }

    const now = Math.floor(Date.now() / 1000)
    return now >= payload.exp
  }

  async _refreshAccessToken(workerName = 'user-auth-worker'): Promise<string | null> {
    if (!this.client) {
      return null
    }

    const refreshToken = await this.getRefreshToken()
    if (!refreshToken) {
      return null
    }

    if (this.isTokenExpired(refreshToken)) {
      await this.clearTokens()
      return null
    }

    try {
      const response = await this.client.callByName(workerName, 'refreshAccessToken', [
        { refreshToken },
      ])

      if (!response.accessToken) {
        return null
      }

      await this.setAccessToken(response.accessToken)
      // Save rotated refresh token if server issued a new one
      if (response.refreshToken) {
        await this.setRefreshToken(response.refreshToken)
      }
      return response.accessToken
    } catch (error: any) {
      // Only clear tokens for auth rejection (401). Transient errors keep tokens intact.
      if (error?.code === 401) {
        await this.clearTokens()
      }
      return null
    }
  }

  async getValidAccessToken(workerName = 'user-auth-worker'): Promise<string | null> {
    const token = await this.getAccessToken()

    if (token && !this.isTokenExpired(token)) {
      return token
    }

    // No token or expired — try refresh
    if (this.refreshPromise) {
      return await this.refreshPromise
    }

    this.refreshPromise = this._refreshAccessToken(workerName)

    try {
      const newToken = await this.refreshPromise
      return newToken
    } finally {
      this.refreshPromise = null
    }
  }

  async isLoggedIn(): Promise<boolean> {
    const token = await this.getValidAccessToken()
    if (token) return true
    // Even if getValidAccessToken failed (e.g. transient network error),
    // the user is still logged in if we have a non-expired refresh token
    const refreshToken = await this.getRefreshToken()
    return !!refreshToken && !this.isTokenExpired(refreshToken)
  }

  async getUserInfo(): Promise<any> {
    const token = await this.getValidAccessToken()
    if (!token) {
      return null
    }

    const payload = this.decodeJWT(token)
    if (!payload) {
      return null
    }

    return {
      sub: payload.sub,
      provider: payload.sub_type || payload.provider,
      identifier: payload.identifier,
      email: payload.email || payload.identifier,
      name: payload.name || payload.identifier?.split('@')[0],
      picture: payload.picture,
      exp: payload.exp,
      iat: payload.iat,
    }
  }

  async getLoginMethod(): Promise<string | null> {
    return await this.storage.get(this._getKey('login_method'))
  }

  async setLoginMethod(method: string): Promise<void> {
    await this.storage.set(this._getKey('login_method'), method)
  }

  async clearLoginMethod(): Promise<void> {
    try {
      await this.storage.remove(this._getKey('login_method'))
    } catch (error) {
      console.error('[AuthManager] Failed to clear login method:', error)
    }
  }

  async getLoggedInFlag(): Promise<boolean> {
    const flag = await this.storage.get(this._getKey('logged_in'))
    return flag === 'true' || flag === ('true' as any)
  }

  async setLoggedInFlag(value: boolean): Promise<void> {
    await this.storage.set(this._getKey('logged_in'), value ? 'true' : 'false')
  }

  async clearLoggedInFlag(): Promise<void> {
    try {
      await this.storage.remove(this._getKey('logged_in'))
    } catch (error) {
      console.error('[AuthManager] Failed to clear logged in flag:', error)
    }
  }

  getStorageKeys(): string[] {
    return [
      this._getKey('access_token'),
      this._getKey('refresh_token'),
      this._getKey('login_method'),
      this._getKey('logged_in'),
    ]
  }

  async logout(): Promise<void> {
    await Promise.all([this.clearTokens(), this.clearLoginMethod(), this.clearLoggedInFlag()])
  }
}
